import { useNavigate, useParams, Link } from "react-router";
import { Button } from "@nosible/shared/components/ui/button";
import { useSession } from "@/account/lib/auth-client";
import { trpc } from "@/account/lib/trpc";
import { ArrowLeft, CreditCard, Plus, UserMinus } from "lucide-react";
import { InviteMemberDialog } from "../components/InviteMemberDialog";
import { AccountPageHeader } from "@/account/components/AccountPageHeader";

export function OrganizationPage() {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const { data: session } = useSession();

  const {
    data: organization,
    isLoading,
    error,
  } = trpc.organization.getBySlug.useQuery(
    { slug: slug! },
    { enabled: !!session && !!slug }
  );

  const {
    data: members,
    isLoading: membersLoading,
    refetch: membersRefetch,
  } = trpc.organization.getMembersBySlug.useQuery(
    { slug: slug! },
    { enabled: !!session && !!slug }
  );

  // Get all subscriptions for this organization
  const { data: subscriptions } = trpc.organization.getSubscriptions.useQuery(
    { organizationId: organization?.id! },
    { enabled: !!organization?.id }
  );
  const { data: organizationSummaries } = trpc.organization.list.useQuery(undefined, {
    enabled: !!session,
  });

  const removeMember = trpc.organization.removeMember.useMutation({
    onSuccess: () => void membersRefetch(),
  });

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-silica">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4">
        <div className="text-red-400">{error.message}</div>
        <Button variant="outline" onClick={() => navigate("/organizations")}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Organizations
        </Button>
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4">
        <div className="text-chromium-mist">Organization not found</div>
        <Button variant="outline" onClick={() => navigate("/organizations")}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Organizations
        </Button>
      </div>
    );
  }

  const canManageOrganization =
    organization.currentUserRole === "owner" ||
    organization.currentUserRole === "admin" ||
    session?.user.role === "admin";

  const summary = organizationSummaries?.find((item) => item.slug === slug);
  const activeSubscriptions = (subscriptions ?? []).filter((sub) => !["canceled", "cancelled"].includes(sub.status.toLowerCase()));
  const currentUserId = session?.user.id;
  const currentUserEmail = session?.user.email?.trim().toLowerCase();

  return (
    <div className="account-ui-page account-organization-page">
      <AccountPageHeader
        title={organization.name}
        description={organization.slug}
        titleAdornment={<span className={`account-role account-role--${organization.currentUserRole}`}>{organization.currentUserRole?.toUpperCase()}</span>}
      />

      <div className="account-metrics-strip">
        <div><span>MEMBERS</span><strong>{organization.memberCount}</strong></div>
        <div><span>SUBSCRIPTIONS</span><strong>{activeSubscriptions.length}</strong></div>
        <div><span>API KEYS</span><strong>{summary?.apiKeyCount ?? 0}</strong></div>
        <div><span>CREATED</span><strong>{organization.createdAt ? new Date(organization.createdAt).toISOString().slice(0, 10) : "—"}</strong></div>
      </div>

      <section className="account-data-section">
        <div className="account-section-heading"><span><b>01</b> SUBSCRIPTIONS</span></div>
        <div className="account-subscription-empty">
          <CreditCard aria-hidden="true" />
          <div><strong>{activeSubscriptions.length ? "Active subscriptions" : "No active subscriptions"}</strong><span>Organization subscriptions and API access</span></div>
          {canManageOrganization && <Button asChild><Link to={`/organizations/${slug}/new-subscription`}><Plus aria-hidden="true" /> CREATE SUBSCRIPTION</Link></Button>}
        </div>
      </section>

      <section className="account-data-section">
        <div className="account-section-heading">
          <span><b>02</b> MEMBERS</span>
          {canManageOrganization && <InviteMemberDialog organizationId={organization.id} organizationName={organization.name} />}
        </div>
        {membersLoading ? <p className="account-loading-copy">Loading members...</p> : (
          <div className="account-members-table">
            <div className="account-members-table__head"><span>NAME</span><span>EMAIL</span><span>ROLE</span><span>JOINED</span><span /></div>
            {members?.map((member) => {
              const isCurrentMember =
                member.userId === currentUserId ||
                (!!currentUserEmail && member.userEmail.trim().toLowerCase() === currentUserEmail);

              return (
                <div className="account-members-table__row" key={member.id}>
                <strong>{member.userName || "—"}</strong>
                <span>{member.userEmail}</span>
                <span className="account-role">{member.role.toUpperCase()}</span>
                <span>{member.createdAt ? new Date(member.createdAt).toISOString().slice(0, 10) : "—"}</span>
                <span className="account-member-action">
                  {!isCurrentMember && member.role !== "owner" && canManageOrganization && <Button variant="ghost" size="sm" disabled={removeMember.isPending} onClick={() => { if (window.confirm(`Remove ${member.userEmail} from ${organization.name}?`)) removeMember.mutate({ organizationId: organization.id, userId: member.userId }); }}><UserMinus aria-hidden="true" /> REMOVE</Button>}
                  {isCurrentMember && "YOU"}
                  {!isCurrentMember && member.role === "owner" && "OWNER"}
                </span>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
